import { useCallback, useEffect, useRef } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

interface UseDictationArgs {
  isDisabled: boolean;
  onValueChange: (value: string) => void;
  value: string;
}

export default function useDictation({
  isDisabled,
  onValueChange,
  value,
}: UseDictationArgs) {
  const {
    browserSupportsContinuousListening,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
    listening,
    resetTranscript,
    transcript,
  } = useSpeechRecognition();

  // The transcript only holds what was spoken since listening started, so the text that
  // was in the composer at that moment is kept aside and the transcript is appended to it.
  const valueBeforeDictationRef = useRef('');
  // The last composer text dictation itself produced. Anything else means the user typed.
  const expectedValueRef = useRef(value);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onValueChangeRef = useRef(onValueChange);
  onValueChangeRef.current = onValueChange;

  // Snapshot when listening actually begins, not on the click, so text typed while the
  // browser's mic-permission prompt was open is kept.
  useEffect(() => {
    if (!listening) {
      return;
    }

    valueBeforeDictationRef.current = valueRef.current.trimEnd();
    expectedValueRef.current = valueRef.current;
  }, [listening]);

  useEffect(() => {
    if (
      !listening ||
      transcript === '' ||
      valueRef.current !== expectedValueRef.current
    ) {
      return;
    }

    const valueBeforeDictation = valueBeforeDictationRef.current;
    const separator = valueBeforeDictation === '' ? '' : ' ';
    const dictatedValue = `${valueBeforeDictation}${separator}${transcript}`;

    expectedValueRef.current = dictatedValue;
    onValueChangeRef.current(dictatedValue);
  }, [listening, transcript]);

  // Dictation rebuilds the draft from its own transcript, so a manual edit would be
  // overwritten by the next result. Stop instead.
  useEffect(() => {
    if (listening && value !== expectedValueRef.current) {
      void SpeechRecognition.abortListening();
    }
  }, [listening, value]);

  useEffect(() => {
    if (isDisabled && listening) {
      void SpeechRecognition.abortListening();
    }
  }, [isDisabled, listening]);

  useEffect(
    () => () => {
      void SpeechRecognition.abortListening();
    },
    [],
  );

  const startDictation = useCallback(async () => {
    resetTranscript();
    await SpeechRecognition.startListening({
      continuous: browserSupportsContinuousListening,
    });
  }, [browserSupportsContinuousListening, resetTranscript]);

  const stopDictation = useCallback(() => SpeechRecognition.stopListening(), []);

  return {
    isDictating: listening,
    isDictationSupported: browserSupportsSpeechRecognition,
    isMicrophoneBlocked: !isMicrophoneAvailable,
    startDictation,
    stopDictation,
  };
}
